/**
 * One cell edit, said once — the sentence, and the way back.
 *
 * The list and the board edit the same fields on the same tasks through the same
 * `CellEdit` family, so "what just happened" and "what puts it back" are facts about
 * the *edit*, not about the surface it was made on. They lived inside `TaskList`,
 * which is why a due date changed on a board card wrote nothing at all: no sentence
 * on success, and — because `CellDue`'s `onError` is optional — **silence on a
 * refusal**, which is the one case a reader has to be told about.
 *
 * So the pair moved here, beside `taskNotice.tsx` rather than inside it: the notice
 * is the strip and the live region, and this is the vocabulary one edit writes into
 * it. `components/studio/TaskCardFull.tsx` takes the two handlers as props instead of
 * importing this, because a primitive that reaches into a page for its words is a
 * primitive that cannot be drawn anywhere else.
 *
 * A **move** made in the Status cell is said here too (`movedLabel`, `moved`), for
 * the same reason and with one difference: it carries no way back. A move is taken
 * back by another move, and the cell that made it goes on to offer the ones there are.
 *
 * ## The way back is conditional
 *
 * The strip's Undo does not expire, and what it holds is the value a field had
 * **before** an edit. Pressed after the task has changed again — the reader started
 * it, a colleague reassigned it, another tab edited it — it used to write that older
 * value over the newer one, and "Undo" of an assignment on a task since started took
 * live work off its holder with no question. So every edit carries `since`, the
 * `updated_at` its own write answered with, and the Undo sends it back as
 * `if_unchanged_since`: the API makes the change only if nothing has been written to
 * the task in between, and answers 409 `Conflict` when something has
 * (`taskNotice.tsx` `unlessChanged` is that refusal, in the page's words).
 */

import { QUEUE_WORD, priorityWord } from '../../../components/studio/taskWords.js'
import { useStudioAction } from '../../../api/studio.js'
import { unlessChanged, useTaskNotice, type TaskUndo } from './taskNotice.js'

import type { AssigneeKind, TaskEvent, TaskQueue, TaskView } from '@fareast/shared'

/** Who held a task before a reassignment — the pair `tasks_assign` takes back. */
export type AssigneeUndo = {
  readonly kind: AssigneeKind
  readonly id: string | null
}

/** What every cell edit says about the write it was, whichever field it changed. */
type Written = {
  readonly taskRef: string
  /**
   * The task's `updated_at` as the edit's own answer carried it
   * (`components/studio/CellEdit.tsx` `stampOf`): the one version of the task the way
   * back may be written over. Null when the answer carried none — and then no Undo is
   * offered at all, because one with nothing to send is the unconditional write this
   * field exists to end.
   */
  readonly since: string | null
}

/** One cell edit, as both halves of the sentence need it: which field, and what moved. */
export type FieldUndo = Written &
  (
    | {
        readonly kind: 'priority'
        readonly previous: number
        readonly next: number
      }
    | {
        readonly kind: 'due'
        readonly previous: string | null
        readonly next: string | null
      }
    | {
        readonly kind: 'queue'
        readonly previous: TaskQueue
        readonly next: TaskQueue
      }
    | {
        readonly kind: 'assign'
        readonly previous: AssigneeUndo
        readonly next: AssigneeUndo
        /** Somebody was taken off live work: the task went back on the queue (#48). */
        readonly released?: boolean | undefined
      }
  )

/** What one cell edit says, in the words the strip carries. */
export function savedLabel(undo: FieldUndo): string {
  return undo.kind === 'priority'
    ? `${undo.taskRef} is now ${priorityWord(undo.next)}.`
    : undo.kind === 'due'
      ? undo.next === null
        ? `${undo.taskRef} due date removed.`
        : `${undo.taskRef} due date updated.`
      : undo.kind === 'queue'
        ? `${undo.taskRef} is now on ${QUEUE_WORD[undo.next]}.`
        : `${undo.taskRef} assignee updated.`
}

/** One move made on a task: which, and where the write's answer says it stands now. */
export type TaskMoved = {
  readonly taskRef: string
  readonly move: TaskEvent
  /** Null when the answer did not say; the sentence is then the move's usual one. */
  readonly landed: TaskView['status'] | null
}

/**
 * Where a move leaves a task, as the predicate of the sentence that names it.
 *
 * Total over the machine's events, so one added to `taskMachine` is a type error here
 * rather than a move that says nothing. "is yours" and "is under way" were the row
 * buttons' words before the cell said anything; "is back on the queue" is what an
 * Unassign that released the task already says (`saved` below) — one outcome, one
 * sentence, whichever control caused it.
 */
const MOVED_WORDS: Readonly<Record<TaskEvent, string>> = {
  claim: 'is yours',
  start: 'is under way',
  block: 'is blocked',
  unblock: 'is unblocked',
  submit_for_review: 'is in review',
  complete: 'is done',
  approve_review: 'is done',
  release: 'is back on the queue',
  lease_expired: 'is back on the queue',
  cancel: 'is cancelled',
}

/**
 * What one move says, in the words the strip carries.
 *
 * One function for the row's buttons (`RowActions`) and the Status cell, because they
 * make the same moves on the same row, and a second spelling for the same call from
 * the next column would be the two-vocabularies problem `taskWords.ts` `moveLabel`
 * was written to end.
 *
 * The sentence is about the **task**, not the click — where it is now. Which is why
 * `complete` reads the answer: `tasks_complete` on a task that needs a person sends
 * it to review instead (`taskWords.ts` `CLOSING_WORDS`), and "is done." over a task
 * waiting for approval would be the page stating something that did not happen.
 */
export function movedLabel({ taskRef, move, landed }: TaskMoved): string {
  const words =
    move === 'complete' && landed === 'review' ? MOVED_WORDS.submit_for_review : MOVED_WORDS[move]
  return `${taskRef} ${words}.`
}

/**
 * The handlers a cell editor is given, wherever it is drawn.
 *
 * `saved` writes the page's one line and the way back; `moved` writes the line for a
 * move, which has no way back; `failed` writes the refusal to the same line, because
 * a reader looking for what happened should not have to know which of them it was
 * before they know where to look.
 */
export type FieldSave = {
  readonly saved: (undo: FieldUndo) => void
  readonly moved: (moved: TaskMoved) => void
  readonly failed: (message: string) => void
}

export function useFieldSave(): FieldSave {
  const notice = useTaskNotice()
  const restoreUpdate = useStudioAction('tasks_update')
  const restoreAssign = useStudioAction('tasks_assign')

  /**
   * What puts one cell edit back, as the page's notice takes it — **if the task is
   * still what that edit left**. `since` goes on the one write there is.
   */
  const undoField = (undo: FieldUndo, since: string): TaskUndo => {
    const still = { task_ref: undo.taskRef, if_unchanged_since: since }
    return () =>
      unlessChanged(undo.taskRef, () => {
        if (undo.kind === 'assign') {
          return restoreAssign.mutateAsync({
            ...still,
            assignee_kind: undo.previous.kind,
            assignee_id: undo.previous.id,
          })
        }
        if (undo.kind === 'priority') {
          return restoreUpdate.mutateAsync({ ...still, priority: undo.previous })
        }
        if (undo.kind === 'due') {
          return restoreUpdate.mutateAsync({ ...still, due_at: undo.previous })
        }
        return restoreUpdate.mutateAsync({ ...still, queue: undo.previous })
      })
  }

  return {
    saved: (undo) => {
      /* Putting the name back would not put the claim back, so there is no Undo to offer. */
      if (undo.kind === 'assign' && undo.released === true) {
        notice.report({
          label: movedLabel({ taskRef: undo.taskRef, move: 'release', landed: 'open' }),
          ref: undo.taskRef,
        })
        return
      }
      notice.report({
        label: savedLabel(undo),
        /* No stamp, no way back: see `Written.since`. The save is still said. */
        undo: undo.since === null ? undefined : undoField(undo, undo.since),
        ref: undo.taskRef,
      })
    },
    /* A report replaces the line, so an Undo from before this move goes with it. */
    moved: (moved) => {
      notice.report({ label: movedLabel(moved), ref: moved.taskRef })
    },
    failed: notice.fail,
  }
}
